import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { BrandWatermark, BrandHeader } from '@/components/BrandWatermark';
import { UserBadgesLoader } from '@/components/UserBadges';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchSignals();
    fetchStats();
  }, [filter]);

  const fetchSignals = async () => {
    try {
      const url = filter === 'all' ? `${API}/signals` : `${API}/signals?status=${filter}`;
      const response = await axios.get(url);
      setSignals(response.data);
    } catch (error) {
      toast.error('Error al cargar señales');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/signals/stats/results`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#020402]">
      <Navbar />
      <BrandWatermark />
      
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4 text-emerald-50"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
              data-testid="dashboard-title"
            >
              <span className="flex items-center space-x-3">
                <span>SEÑALES DE TRADING</span>
                <UserBadgesLoader size="md" />
              </span>
            </h1>
            <p className="text-emerald-100/70">Revisa las últimas señales publicadas por nuestro equipo de análisis</p>
          </div>

          {/* Stats Table */}
          {stats && (
            <div className="glass-panel rounded-lg p-6 mb-8" data-testid="stats-table">
              <h2 
                className="text-2xl font-bold mb-4 text-emerald-50"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                TABLA DE RESULTADOS
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-4">
                  <p className="text-emerald-100/60 text-sm mb-1">Total Operaciones</p>
                  <p className="text-3xl font-bold text-emerald-50 font-mono">{stats.won + stats.lost}</p>
                </div>
                <div className="bg-black/30 border border-green-500/20 rounded-lg p-4">
                  <p className="text-emerald-100/60 text-sm mb-1">Ganadas</p>
                  <p className="text-3xl font-bold text-green-400 font-mono">{stats.won}</p>
                </div>
                <div className="bg-black/30 border border-red-500/20 rounded-lg p-4">
                  <p className="text-emerald-100/60 text-sm mb-1">Perdidas</p>
                  <p className="text-3xl font-bold text-red-400 font-mono">{stats.lost}</p>
                </div>
                <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-4">
                  <p className="text-emerald-100/60 text-sm mb-1">Win Rate</p>
                  <p className="text-3xl font-bold text-emerald-400 font-mono">{stats.win_rate}%</p>
                </div>
              </div>
              
              {/* Detailed Results Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-emerald-500/20">
                      <th className="py-3 px-4 text-emerald-100/70 font-semibold">Par</th>
                      <th className="py-3 px-4 text-emerald-100/70 font-semibold">Tipo</th>
                      <th className="py-3 px-4 text-emerald-100/70 font-semibold">Resultado</th>
                      <th className="py-3 px-4 text-emerald-100/70 font-semibold">Pips</th>
                      <th className="py-3 px-4 text-emerald-100/70 font-semibold">Ganancia %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signals.filter(s => s.result).map((signal) => (
                      <tr key={signal.id} className="border-b border-emerald-500/10 hover:bg-emerald-500/5" data-testid={`result-row-${signal.id}`}>
                        <td className="py-3 px-4 font-mono text-emerald-50">{signal.symbol}</td>
                        <td className="py-3 px-4">
                          <span className={signal.signal_type === 'buy' ? 'text-green-400' : 'text-red-400'}>
                            {signal.signal_type === 'buy' ? '↑ COMPRA' : '↓ VENTA'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded text-sm font-bold ${
                            signal.result === 'won' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {signal.result === 'won' ? '✓ GANADA' : '✗ PERDIDA'}
                          </span>
                        </td>
                        <td className={`py-3 px-4 font-mono font-bold ${signal.pips >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {signal.pips > 0 ? '+' : ''}{signal.pips}
                        </td>
                        <td className={`py-3 px-4 font-mono font-bold ${signal.profit_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {signal.profit_percentage > 0 ? '+' : ''}{signal.profit_percentage}%
                        </td>
                      </tr>
                    ))}
                    {signals.filter(s => s.result).length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-8 px-4 text-center text-emerald-100/50">
                          No hay operaciones cerradas aún
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex space-x-3 mb-8">
            <Button
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              className={filter === 'all' 
                ? 'bg-emerald-500 text-black hover:bg-emerald-400' 
                : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'}
              data-testid="filter-all"
            >
              Todas
            </Button>
            <Button
              onClick={() => setFilter('active')}
              variant={filter === 'active' ? 'default' : 'outline'}
              className={filter === 'active' 
                ? 'bg-emerald-500 text-black hover:bg-emerald-400' 
                : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'}
              data-testid="filter-active"
            >
              Activas
            </Button>
            <Button
              onClick={() => setFilter('closed')}
              variant={filter === 'closed' ? 'default' : 'outline'}
              className={filter === 'closed' 
                ? 'bg-emerald-500 text-black hover:bg-emerald-400' 
                : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'}
              data-testid="filter-closed"
            >
              Cerradas
            </Button>
          </div>

          {/* Signals Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent"></div>
              <p className="text-emerald-100/70 mt-4">Cargando señales...</p>
            </div>
          ) : signals.length === 0 ? (
            <div className="glass-panel rounded-lg p-12 text-center" data-testid="no-signals">
              <TrendingUp className="h-16 w-16 text-emerald-500/50 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-emerald-100">No hay señales disponibles</h3>
              <p className="text-emerald-100/60">Las nuevas señales aparecerán aquí cuando sean publicadas.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="signals-grid">
              {signals.map((signal) => (
                <div 
                  key={signal.id}
                  onClick={() => navigate(`/signals/${signal.id}`)}
                  className="glass-panel rounded-lg p-6 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer"
                  data-testid={`signal-card-${signal.id}`}
                >
                  {/* Signal Type Badge */}
                  <div className="flex justify-between items-start mb-4">
                    <span className={signal.signal_type === 'buy' ? 'signal-badge-buy' : 'signal-badge-sell'}>
                      {signal.signal_type === 'buy' ? (
                        <><ArrowUpRight className="inline h-3 w-3" /> COMPRA</>
                      ) : (
                        <><ArrowDownRight className="inline h-3 w-3" /> VENTA</>
                      )}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      signal.status === 'active' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {signal.status === 'active' ? 'ACTIVA' : 'CERRADA'}
                    </span>
                  </div>

                  {/* Title & Symbol */}
                  <h3 className="text-xl font-bold mb-1 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    {signal.title}
                  </h3>
                  <p className="text-2xl font-mono font-bold text-emerald-400 mb-4">{signal.symbol}</p>

                  {/* Prices */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-emerald-100/60">Entrada:</span>
                      <span className="font-mono text-emerald-50">${signal.entry_price}</span>
                    </div>
                    {signal.stop_loss && (
                      <div className="flex justify-between">
                        <span className="text-emerald-100/60">Stop Loss:</span>
                        <span className="font-mono text-red-400">${signal.stop_loss}</span>
                      </div>
                    )}
                    {signal.take_profits?.length > 0 ? (
                      signal.take_profits.map((tp, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="text-emerald-100/60">TP{i + 1}:</span>
                          <span className="font-mono text-emerald-400">${tp}</span>
                        </div>
                      ))
                    ) : signal.take_profit ? (
                      <div className="flex justify-between">
                        <span className="text-emerald-100/60">Take Profit:</span>
                        <span className="font-mono text-emerald-400">${signal.take_profit}</span>
                      </div>
                    ) : null}
                    {signal.risk_reward_ratio && (
                      <div className="flex justify-between pt-2 border-t border-emerald-500/20">
                        <span className="text-emerald-100/60">Riesgo/Recompensa:</span>
                        <span className="font-mono text-emerald-400 font-bold">1:{signal.risk_reward_ratio}</span>
                      </div>
                    )}
                  </div>

                  {/* Chart Preview */}
                  {signal.chart_image_url && (
                    <div className="mt-4 border border-emerald-500/20 rounded overflow-hidden">
                      <img 
                        src={signal.chart_image_url} 
                        alt="Chart"
                        className="w-full h-32 object-cover"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}